// Types
export type Employee = {
  id: string;
  name: string;
  email: string;
  phone: string;
  position: string;
  department: string;
  avatar?: string;
  availability: {
    monday: { available: boolean; hours?: [number, number] };
    tuesday: { available: boolean; hours?: [number, number] };
    wednesday: { available: boolean; hours?: [number, number] };
    thursday: { available: boolean; hours?: [number, number] };
    friday: { available: boolean; hours?: [number, number] };
    saturday: { available: boolean; hours?: [number, number] };
    sunday: { available: boolean; hours?: [number, number] };
  };
  preferences: {
    maxHoursPerWeek: number;
    consecutiveDays: number;
  };
};

export type Shift = {
  id: string;
  employeeId: string;
  startTime: Date;
  endTime: Date;
  position: string;
  notes?: string;
};

export type Department = {
  id: string;
  name: string;
  color: string;
};

// Mock data
export const departments: Department[] = [
  { id: '1', name: 'Sales', color: '#3B82F6' },
  { id: '2', name: 'Customer Service', color: '#10B981' },
  { id: '3', name: 'Management', color: '#6366F1' },
  { id: '4', name: 'Kitchen', color: '#F59E0B' },
  { id: '5', name: 'Delivery', color: '#EC4899' },
];

export const employees: Employee[] = [
  {
    id: '1',
    name: 'Emma Johnson',
    email: 'emma@example.com',
    phone: '+31 6 12345678',
    position: 'Sales Associate',
    department: 'Sales',
    availability: {
      monday: { available: true, hours: [9, 17] },
      tuesday: { available: true, hours: [9, 17] },
      wednesday: { available: true, hours: [9, 17] },
      thursday: { available: true, hours: [9, 17] },
      friday: { available: true, hours: [9, 17] },
      saturday: { available: false },
      sunday: { available: false },
    },
    preferences: {
      maxHoursPerWeek: 40,
      consecutiveDays: 5,
    },
  },
  {
    id: '2',
    name: 'Liam Smith',
    email: 'liam@example.com',
    phone: '+31 6 23456789',
    position: 'Customer Service Representative',
    department: 'Customer Service',
    availability: {
      monday: { available: true, hours: [12, 20] },
      tuesday: { available: true, hours: [12, 20] },
      wednesday: { available: false },
      thursday: { available: true, hours: [12, 20] },
      friday: { available: true, hours: [12, 20] },
      saturday: { available: true, hours: [10, 18] },
      sunday: { available: false },
    },
    preferences: {
      maxHoursPerWeek: 32,
      consecutiveDays: 4,
    },
  },
  {
    id: '3',
    name: 'Olivia Brown',
    email: 'olivia@example.com',
    phone: '+31 6 34567890',
    position: 'Store Manager',
    department: 'Management',
    availability: {
      monday: { available: true, hours: [8, 16] },
      tuesday: { available: true, hours: [8, 16] },
      wednesday: { available: true, hours: [8, 16] },
      thursday: { available: true, hours: [8, 16] },
      friday: { available: true, hours: [8, 16] },
      saturday: { available: true, hours: [10, 14] },
      sunday: { available: false },
    },
    preferences: {
      maxHoursPerWeek: 40,
      consecutiveDays: 5,
    },
  },
  {
    id: '4',
    name: 'Noah Garcia',
    email: 'noah@example.com',
    phone: '+31 6 45678901',
    position: 'Chef',
    department: 'Kitchen',
    availability: {
      monday: { available: false },
      tuesday: { available: true, hours: [14, 22] },
      wednesday: { available: true, hours: [14, 22] },
      thursday: { available: true, hours: [14, 22] },
      friday: { available: true, hours: [14, 22] },
      saturday: { available: true, hours: [14, 22] },
      sunday: { available: true, hours: [14, 22] },
    },
    preferences: {
      maxHoursPerWeek: 40,
      consecutiveDays: 4,
    },
  },
  {
    id: '5',
    name: 'Sophia Miller',
    email: 'sophia@example.com',
    phone: '+31 6 56789012',
    position: 'Delivery Driver',
    department: 'Delivery',
    availability: {
      monday: { available: true, hours: [16, 24] },
      tuesday: { available: true, hours: [16, 24] },
      wednesday: { available: true, hours: [16, 24] },
      thursday: { available: false },
      friday: { available: false },
      saturday: { available: true, hours: [12, 24] },
      sunday: { available: true, hours: [12, 24] },
    },
    preferences: {
      maxHoursPerWeek: 30,
      consecutiveDays: 3,
    },
  },
];

export const generateMockShifts = (startDate: Date, days: number = 14): Shift[] => {
  const shifts: Shift[] = [];
  const positions = ['Sales Associate', 'Customer Service Representative', 'Store Manager', 'Chef', 'Delivery Driver'];

  for (let day = 0; day < days; day++) {
    const currentDate = new Date(startDate);
    currentDate.setDate(currentDate.getDate() + day);
    
    const shiftsPerDay = Math.floor(Math.random() * 3) + 3;
    
    for (let i = 0; i < shiftsPerDay; i++) {
      const employeeIndex = Math.floor(Math.random() * employees.length);
      const employee = employees[employeeIndex];
      
      const startHour = 8 + Math.floor(Math.random() * 8);
      const durationHours = 4 + Math.floor(Math.random() * 5);
      
      const shiftStartTime = new Date(currentDate);
      shiftStartTime.setHours(startHour, 0, 0, 0);
      
      const shiftEndTime = new Date(shiftStartTime);
      shiftEndTime.setHours(shiftStartTime.getHours() + durationHours);
      
      shifts.push({
        id: `shift-${day}-${i}`,
        employeeId: employee.id,
        startTime: shiftStartTime,
        endTime: shiftEndTime,
        position: employee.position,
        notes: Math.random() > 0.7 ? 'Training shift' : undefined,
      });
    }
  }
  
  return shifts;
};

const today = new Date();
today.setHours(0, 0, 0, 0);

export const shifts = generateMockShifts(today);

export const getStats = () => {
  return {
    totalEmployees: employees.length,
    scheduledShifts: shifts.length,
    hoursScheduled: shifts.reduce((total, shift) => {
      const hours = (shift.endTime.getTime() - shift.startTime.getTime()) / (1000 * 60 * 60);
      return total + hours;
    }, 0),
    departments: departments.length,
    upcomingTimeOffRequests: 3,
    openShifts: 5,
  };
};

export const fetchDashboardData = async () => {
  return new Promise<{
    totalUsers: number;
    activeUsersToday: number;
    averageSessionDuration: string;
    newSignupsThisWeek: number;
    lastSignup: string;
    lastScheduleChange: string;
  }>((resolve) => {
    setTimeout(() => {
      resolve({
        totalUsers: 120,
        activeUsersToday: 45,
        averageSessionDuration: '24 min',
        newSignupsThisWeek: 15,
        lastSignup: '2 uur geleden',
        lastScheduleChange: '15 minuten geleden',
      });
    }, 500);
  });
};
